import { useState, useRef, useEffect } from "react";
import {
  ChevronDown,
  User,
  Settings,
  LogOut,
  Github,
  School,
} from "lucide-react";
import { useAuth } from "../../app/AuthContext";

interface ProfileMenuProps {
  onLogout?: () => void;
  onProfileClick?: () => void;
  onSettingsClick?: () => void;
  onSchoolProfileClick?: () => void;
}

export default function ProfileMenu({
  onLogout,
  onProfileClick,
  onSettingsClick,
  onSchoolProfileClick,
}: ProfileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();
  const [avatarFailed, setAvatarFailed] = useState(false);

  // Fecha o menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
    if (onLogout) {
      onLogout();
    }
  };

  const handleProfileClick = () => {
    setIsOpen(false);
    if (onProfileClick) {
      onProfileClick();
    }
  };

  const handleSettingsClick = () => {
    setIsOpen(false);
    if (onSettingsClick) {
      onSettingsClick();
    }
  };

  const getInitials = () => {
    const name = user?.name?.trim();

    if (name) {
      const parts = name.split(" ").filter(Boolean);

      const first = parts[0]?.[0];
      const second = parts[1]?.[0];

      if (first && second) {
        return `${first}${second}`.toUpperCase();
      }

      if (first) {
        return first.toUpperCase();
      }
    }

    return user?.email?.[0]?.toUpperCase() ?? "U";
  };

  const avatarUrl = user?.avatarUrl?.trim() ? user.avatarUrl.trim() : null;

  return (
    <div className="relative" ref={menuRef}>
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 sm:gap-2 hover:bg-(--bg-accent) rounded-lg px-2 sm:px-3 py-2 transition relative group"
      >
        <div
          className="w-8 h-8 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-sm ring-2 ring-transparent sm:ring-0 active:ring-(--primary-color) sm:active:ring-0 shadow-sm sm:shadow-none overflow-hidden"
          style={{ backgroundColor: "var(--primary-color)" }}
        >
          {avatarUrl && !avatarFailed ? (
            <img
              src={avatarUrl}
              alt="Avatar"
              className="w-full h-full object-cover"
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            getInitials()
          )}
          {/* Mobile indicator for clickability */}
          <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 border-2 border-(--card-bg) rounded-full sm:hidden"></div>
        </div>
        <ChevronDown
          className={`hidden sm:block w-4 h-4 transition-transform text-(--text-tertiary) ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-(--card-bg) rounded-xl shadow-2xl border border-(--border-color) overflow-hidden z-50">
          {/* User Info Section */}
          <div className="p-4 border-b border-(--border-color) bg-(--bg-secondary)">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg overflow-hidden"
                style={{ backgroundColor: "var(--primary-color)" }}
              >
                {avatarUrl && !avatarFailed ? (
                  <img
                    src={avatarUrl}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                    onError={() => setAvatarFailed(true)}
                  />
                ) : (
                  getInitials()
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate text-(--text-primary)">
                  {user?.name || "Professor"}
                </p>
                <p className="text-sm truncate text-(--text-tertiary)">
                  {user?.email || "email@exemplo.com"}
                </p>
                {user?.school && (
                  <p className="text-xs truncate mt-1 text-(--text-tertiary)">
                    📚 {user.school}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            <button
              onClick={handleProfileClick}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-(--bg-accent) transition text-left"
            >
              <User className="w-5 h-5 text-(--text-tertiary)" />
              <div>
                <p className="font-medium text-sm text-(--text-primary)">
                  Meu Perfil
                </p>
                <p className="text-xs text-(--text-tertiary)">
                  Visualizar e editar informações
                </p>
              </div>
            </button>

            <button
              onClick={handleSettingsClick}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-(--bg-accent) transition text-left"
            >
              <Settings className="w-5 h-5 text-(--text-tertiary)" />
              <div>
                <p className="font-medium text-sm text-(--text-primary)">
                  Configurações
                </p>
                <p className="text-xs text-(--text-tertiary)">
                  Preferências e privacidade
                </p>
              </div>
            </button>

            <button
              onClick={() => {
                setIsOpen(false);
                if (onSchoolProfileClick) {
                  onSchoolProfileClick();
                }
              }}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-(--bg-accent) transition text-left"
            >
              <School className="w-5 h-5 text-(--text-tertiary)" />
              <div>
                <p className="font-medium text-sm text-(--text-primary)">
                  Trocar Escola
                </p>
                <p className="text-xs text-(--text-tertiary)">
                  Gerenciar e alterar perfis escolares
                </p>
              </div>
            </button>

            <div className="my-2 h-px bg-(--border-color)"></div>

            <button
              onClick={() => {
                setIsOpen(false);
                window.open(
                  "https://github.com/Felipe-Silva7/assistente-educacional",
                  "_blank",
                );
              }}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-(--bg-accent) transition text-left"
            >
              <Github className="w-5 h-5 text-(--text-tertiary)" />
              <div>
                <p className="font-medium text-sm text-(--text-primary)">
                  Repositório do Projeto
                </p>
                <p className="text-xs text-(--text-tertiary)">
                  Ver código fonte no GitHub
                </p>
              </div>
            </button>

            <div className="my-2 h-px bg-(--border-color)"></div>

            <button
              onClick={handleLogout}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-red-50 dark:hover:bg-red-900/20 transition text-left"
            >
              <LogOut className="w-5 h-5 text-(--danger-color)" />
              <div>
                <p className="font-medium text-sm text-(--danger-color)">
                  Sair
                </p>
                <p className="text-xs text-(--text-tertiary)">
                  Encerrar sessão
                </p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
